import { Add, Close, Save } from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  FormGroup,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Box,
  ButtonGroup,
  Button,
  Divider
} from '@mui/material';
import { useQuery } from 'react-query';
import { Form, Formik, Field } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import React, { FC } from 'react';
import {
  getFacadeTypes,
  getCladdingVersions,
  getAppraisalAlarms,
  createPlanGeneralInfo,
  putPlanGeneralInfo
} from '../../../../fetch';
import { IDropdownResponse, IPlanGeneralInfo, IPlanGeneralInfoPut } from '../../../../models';
import { Loader, SMBSelect } from '../../../../components';

interface IGeneralInformation {
  generalInfo: IPlanGeneralInfo;
  isLoadingGeneralInfo: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchGeneralInfo: () => void;
}

const GeneralInfoSchema = Yup.object().shape({
  planId: Yup.string(),
  isActive: Yup.boolean(),
  planNumber: Yup.number().required('Required'),
  planName: Yup.string().required('Required'),
  facades: Yup.array().min(1, 'At least 1 selection is required'),
  claddingVersions: Yup.array().min(1, 'At least 1 selection is required'),
  appraisalAlarm: Yup.number(),
  parentPlan: Yup.string().max(2, 'Max 2 characters').nullable()
});

export const GeneralInformation: FC<IGeneralInformation> = ({
  generalInfo,
  isLoadingGeneralInfo,
  setDirtyForm,
  refetchGeneralInfo
}) => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { planId }: { planId: string } = useParams();

  const { isLoading: isLoadingFacades, data: facadeTypes } = useQuery<IDropdownResponse[], Error>(
    ['getFacadeTypes'],
    () => getFacadeTypes()
  );

  const { isLoading: isLoadingCladdingVersions, data: claddingVersions } = useQuery<
    IDropdownResponse[],
    Error
  >(['getCladdingVersions'], () => getCladdingVersions());

  const { isLoading: isLoadingAppraisalAlarms, data: appraisalAlarms } = useQuery<
    IDropdownResponse[],
    Error
  >(['getAppraisalAlarms'], () => getAppraisalAlarms());

  return (
    <>
      {isLoadingGeneralInfo && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingGeneralInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            isActive: generalInfo?.isActive ?? true,
            planNumber: generalInfo?.planNumber ?? '',
            planName: generalInfo?.planName ?? '',
            facades: generalInfo?.facades ?? [],
            claddingVersions: generalInfo?.claddingVersions ?? [],
            appraisalAlarmId: generalInfo?.appraisalAlarmId ?? 1,
            parentPlan: generalInfo?.parentPlan ?? ''
          }}
          validationSchema={GeneralInfoSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IPlanGeneralInfoPut = {
                ...values
              };
              if (planId !== 'add') {
                await putPlanGeneralInfo(parseInt(planId), data);
                refetchGeneralInfo();
                enqueueSnackbar('Plan has been updated successfully!', {
                  variant: 'success'
                });
              }
              if (planId === 'add') {
                const planId = await createPlanGeneralInfo(data);
                actions.resetForm();
                enqueueSnackbar('Plan has been created successfully!', {
                  variant: 'success'
                });

                history.push(`/development/plans/manage-plans/${planId}/general-information`);
              }
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Something went wrong. Plan was not updated.', {
                variant: 'error'
              });
            }
          }}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}

                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              color="primary"
                              checked={values.isActive}
                              id="active-inactive"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setFieldValue('isActive', e.target.checked);
                              }}
                            />
                          }
                          label="Active"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" justifyContent="center" spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <Field name={`planNumber`}>
                              {({ field }) => {
                                return (
                                  <TextField
                                    required
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Plan Number"
                                    name="planNumber"
                                    size="small"
                                    InputLabelProps={{
                                      shrink: values.planNumber ? true : false
                                    }}
                                    value={values.planNumber}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('planNumber', e.target.value)}
                                    error={Boolean(touched.planNumber && errors.planNumber)}
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field name={`planName`}>
                              {({ field }) => {
                                return (
                                  <TextField
                                    fullWidth
                                    required={planId === 'add'}
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Plan Name"
                                    name="planName"
                                    size="small"
                                    value={values.planName}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('planName', e.target.value)}
                                    error={Boolean(touched.planName && errors.planName)}
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                          <Grid item xs={12}>
                            <FormControl component="fieldset" variant="standard" required>
                              <FormLabel component="legend">Facades</FormLabel>
                              <FormGroup
                                sx={{
                                  position: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'left',
                                  alignItems: 'start'
                                }}
                              >
                                {!isLoadingFacades &&
                                  facadeTypes &&
                                  facadeTypes.map((facade, index) => {
                                    return (
                                      <FormControlLabel
                                        key={`${index}`}
                                        control={
                                          <Checkbox
                                            checked={values.facades.includes(facade.value)}
                                            onChange={() => {
                                              values.facades.includes(facade.value)
                                                ? setFieldValue(
                                                    'facades',
                                                    values.facades.filter(
                                                      val => val !== facade.value
                                                    )
                                                  )
                                                : setFieldValue('facades', [
                                                    ...values.facades,
                                                    facade.value
                                                  ]);
                                            }}
                                          />
                                        }
                                        label={facade.description}
                                      />
                                    );
                                  })}
                                {touched && touched.facades && errors && errors.facades && (
                                  <FormHelperText error>Required</FormHelperText>
                                )}
                              </FormGroup>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12}>
                            <FormControl component="fieldset" variant="standard" required>
                              <FormLabel component="legend">Cladding Versions</FormLabel>
                              <FormGroup
                                sx={{
                                  position: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'left',
                                  alignItems: 'start'
                                }}
                              >
                                {!isLoadingCladdingVersions &&
                                  claddingVersions &&
                                  claddingVersions.map((cladding, index) => {
                                    return (
                                      <FormControlLabel
                                        key={`${index}`}
                                        control={
                                          <Checkbox
                                            checked={values.claddingVersions.includes(
                                              cladding.value
                                            )}
                                            onChange={() => {
                                              values.claddingVersions.includes(cladding.value)
                                                ? setFieldValue(
                                                    'claddingVersions',
                                                    values.claddingVersions.filter(
                                                      val => val !== cladding.value
                                                    )
                                                  )
                                                : setFieldValue('claddingVersions', [
                                                    ...values.claddingVersions,
                                                    cladding.value
                                                  ]);
                                            }}
                                            color="primary"
                                          />
                                        }
                                        label={cladding.description}
                                      />
                                    );
                                  })}
                                {touched &&
                                  touched.claddingVersions &&
                                  errors &&
                                  errors.claddingVersions && (
                                    <FormHelperText error>Required</FormHelperText>
                                  )}
                              </FormGroup>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              variant="outlined"
                              onChange={e => {
                                setFieldValue('appraisalAlarmId', e.target.value);
                              }}
                              name={`appraisalAlarmId`}
                              label={`Appraisal Alert`}
                              id={`appraisalAlarmId`}
                              value={values.appraisalAlarmId}
                              onBlur={handleBlur}
                              disabled={isLoadingAppraisalAlarms}
                              error={errors && errors.appraisalAlarmId ? true : false}
                            >
                              {appraisalAlarms &&
                                appraisalAlarms.map((appraisalAlarmId: any, index) => {
                                  return (
                                    <MenuItem key={`${index + 1}`} value={appraisalAlarmId.value}>
                                      {appraisalAlarmId.value}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field name={`parentPlan`}>
                              {({ field }) => {
                                return (
                                  <TextField
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Parent Plan Number"
                                    name="parentPlan"
                                    size="small"
                                    inputProps={{ maxLength: 2 }}
                                    InputLabelProps={{
                                      shrink: values.parentPlan ? true : false
                                    }}
                                    value={values.parentPlan}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('parentPlan', e.target.value)}
                                    error={Boolean(touched.parentPlan && errors.parentPlan)}
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={planId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/plans/manage-plans');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};
